import React, { useEffect, useRef, useState } from "react";
import { HomeTopGames, HomeTopStreamers } from "@/components/public/HomeSections";
import GameOfWeek from "@/components/games/GameOfWeek";
import NewsCard from "@/components/public/NewsCard";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Trophy, Eye, Users, CalendarDays, Check } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import AdSlot from "@/components/public/AdSlot";
import { STATUS_STYLE } from "./Tournaments";
import { fmtDateTime, FORMAT_LABEL } from "@/components/tournament/constants";

const FALLBACK_SLIDE = {
  id: "default", title: "Host Your Tournament on 7 Legendary",
  description: "Brackets, groups, schedules and live scores — professional tools for every community organizer.",
  image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  button_label: "Create Tournament", button_link: "/tournaments/create",
};

const reveal = { hidden: { opacity: 0, y: 28 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }) };
const Section = ({ children, className = "", ...rest }) => (
  <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={reveal} className={className} {...rest}>{children}</motion.section>
);

function SmartLink({ to, children, ...p }) {
  if (/^https?:\/\//.test(to || "")) return <a href={to} target="_blank" rel="noopener noreferrer" {...p}>{children}</a>;
  return <Link to={to?.startsWith("/") ? to : "/tournaments/create"} {...p}>{children}</Link>;
}

function StackedTitle({ text }) {
  const words = (text || "").trim().split(" ");
  const last = words.length > 1 ? words.pop() : null;
  return (
    <h1 className="mt-5 font-black uppercase tracking-tight leading-[0.92] text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl" data-testid="hero-title">
      {words.join(" ")}{last && <> <span className="text-[hsl(var(--primary))] text-glow">{last}</span></>}
    </h1>
  );
}

function NovaHero({ slide, news }) {
  return (
    <section className="relative min-h-[82vh] flex flex-col justify-end overflow-hidden bg-[#07090e] pt-24 pb-6" data-testid="hero">
      <img src={mediaUrl(slide.image)} alt={slide.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
      <div className="absolute inset-0 cosmos-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090e] via-[#07090e]/55 to-[#07090e]/30" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_40%,transparent_0%,#07090e_88%)]" />

      <div className="relative z-20 mx-auto max-w-7xl w-full px-4 md:px-6">
        <div className="max-w-3xl">
          <StackedTitle text={slide.title} />
          {slide.description && <p className="mt-5 text-sm sm:text-base text-white/70 max-w-xl leading-relaxed" data-testid="hero-description">{slide.description}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            <SmartLink to={slide.button_link || "/tournaments/create"} data-testid="hero-cta"
              className="group inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 rounded-full bg-white text-black text-sm font-bold hover:bg-[hsl(var(--primary))] transition-colors">
              {slide.button_label || "Create Tournament"} <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </SmartLink>
          </div>
        </div>
      </div>

      {news.length > 0 && (
        <div className="relative z-20 mx-auto max-w-7xl w-full px-4 md:px-6 mt-10 lg:mt-14">
          <div className="flex items-center justify-between mb-3">
            <span className="mono uppercase text-[11px] tracking-[0.22em] text-white/45">Gaming News</span>
            <Link to="/news" className="mono uppercase text-[10px] tracking-[0.22em] text-[hsl(var(--primary))]/70 inline-flex items-center gap-1">View all <ArrowRight size={12} /></Link>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x pb-2">
            {news.map((a) => <NovaNewsCard key={a.id} a={a} />)}
          </div>
        </div>
      )}
    </section>
  );
}

function NovaNewsCard({ a }) {
  return (
    <Link to={`/news/${a.slug}`} data-testid={`hero-news-${a.slug}`}
      className="group glass glass-hover rounded-2xl overflow-hidden w-[260px] sm:w-[300px] flex-none snap-start">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={mediaUrl(a.cover)} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f17] via-transparent to-transparent" />
        <span className="absolute top-3 left-3 mono uppercase text-[10px] tracking-widest px-2 py-1 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold">{a.category}</span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1.5 mono uppercase text-[10px] tracking-widest text-white/40"><Eye size={11} /> {a.views?.toLocaleString() || 0} reads</div>
        <h3 className="mt-2 font-bold text-white leading-snug line-clamp-2 group-hover:text-[hsl(var(--primary))] transition-colors">{a.title}</h3>
      </div>
    </Link>
  );
}

function Header({ eyebrow, title, to, label = "View all" }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div><span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>{eyebrow}</span><h2 className="mt-1.5 text-2xl md:text-3xl font-bold text-white">{title}</h2></div>
      {to && <Link to={to} className="shrink-0 text-sm font-semibold text-white/60 hover:text-white inline-flex items-center gap-1">{label} <ArrowRight size={14} /></Link>}
    </div>
  );
}


function NewsFeed() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(1);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || busy || items.length >= total) return;
      setBusy(true);
      api.get("/news", { params: { limit: 8, skip: items.length } })
        .then((r) => { setTotal(r.data.total); setItems((cur) => [...cur, ...r.data.items.filter((a) => !cur.some((c) => c.id === a.id))]); })
        .catch(() => setTotal(0)).finally(() => setBusy(false));
    }, { rootMargin: "300px" });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [items, total, busy]);
  return (
    <div data-testid="home-news-feed">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{items.map((a) => <NewsCard key={a.id} article={a} />)}</div>
      <div ref={ref} className="py-8 text-center text-sm text-white/40" data-testid="home-feed-sentinel">{busy ? "Loading more…" : items.length && items.length >= total ? "You're all caught up." : ""}</div>
    </div>
  );
}

function TournamentCard({ t, i }) {
  const st = STATUS_STYLE[t.status] || STATUS_STYLE.upcoming;
  return (
    <motion.div variants={reveal} custom={i % 4} initial="hidden" whileInView="show" viewport={{ once: true }} whileHover={{ y: -6 }}>
      <Link to={`/tournaments/${t.slug}`} className="group block card-surface rounded-2xl overflow-hidden h-full" data-testid={`home-tournament-${t.slug}`}>
        <div className="relative aspect-[16/9] overflow-hidden bg-white/5">
          {t.cover && <img src={mediaUrl(t.cover)} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <span className={`absolute top-3 left-3 eyebrow px-2 py-1 rounded text-white ${st.pulse ? "live-dot" : ""}`} style={{ background: st.color }}>{st.label}</span>
          {t.game && <span className="absolute bottom-3 left-3 text-xs font-bold text-white/90">{t.game}</span>}
        </div>
        <div className="p-5">
          <h3 className="font-bold text-white text-lg leading-snug line-clamp-2 group-hover:text-[hsl(var(--primary))] transition-colors">{t.name}</h3>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
            <span className="flex items-center gap-1"><CalendarDays size={12} /> {fmtDateTime(t.start_date)}</span>
            <span className="flex items-center gap-1"><Users size={12} /> {t.max_teams} teams</span>
            <span>{FORMAT_LABEL[t.format]}</span>
          </div>
          {t.prize_pool && <div className="mt-3 text-sm font-bold text-white flex items-center gap-1.5"><Trophy size={14} className="text-[hsl(var(--accent))]" /> {t.prize_pool}</div>}
        </div>
      </Link>
    </motion.div>
  );
}


function ProCTA() {
  const perks = ["Single, double, round robin & group brackets", "Open registration link + QR code", "Live scores, schedule & automatic advancement", "Professional PDF report when you finish"];
  return (
    <Section className="relative rounded-3xl overflow-hidden card-surface grain" data-testid="pro-cta">
      <img src="https://images.unsplash.com/photo-1642104744809-14b986179927?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="relative z-10 grid lg:grid-cols-[1.3fr_1fr] gap-10 p-8 md:p-14 items-center">
        <div>
          <span className="eyebrow" style={{ color: "hsl(var(--accent))" }}>For organizers</span>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white leading-tight">Create Professional Tournament</h2>
          <p className="mt-4 text-white/65 max-w-xl">Run your event like the pros. Build brackets in minutes, let teams sign up with one link, update scores live, and share a beautiful public page with your community.</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {perks.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-white/80"><Check size={16} className="mt-0.5 shrink-0 text-green-400" /> {p}</li>)}
          </ul>
        </div>
        <motion.div whileHover={{ scale: 1.02 }} className="rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-xl p-7">
          <div className="text-sm font-semibold text-white/60">Per tournament</div>
          <div className="mt-2 flex items-end gap-3">
            <span className="text-2xl font-bold text-white/40 line-through" data-testid="cta-old-price">$7</span>
            <span className="text-6xl font-bold font-head text-white" data-testid="cta-price">$5</span>
          </div>
          <span className="mt-3 inline-block eyebrow px-2.5 py-1 rounded-full bg-[hsl(var(--accent))] text-white">Save 28% — limited offer</span>
          <Link to="/tournaments/create" data-testid="home-create-tournament" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black font-bold hover:gap-3 transition-[gap]">Create Tournament <ArrowRight size={18} /></Link>
          <p className="mt-3 text-[11px] text-white/40 text-center">One-time payment. Secure checkout by Stripe.</p>
        </motion.div>
      </div>
    </Section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [top, setTop] = useState([]);
  const [latest, setLatest] = useState([]);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/news", { params: { sort: "views", limit: 7 } }).then((r) => setTop(r.data.items)).catch(() => {});
    api.get("/news", { params: { limit: 9 } }).then((r) => setLatest(r.data.items)).catch(() => {});
    api.get("/tournaments").then((r) => setTours(r.data.items)).catch(() => {});
  }, []);

  const slides = (settings?.hero_slides || []).filter((s) => s.image);
  const hero = slides[0] || FALLBACK_SLIDE;

  const heroNews = (latest.length ? latest : top).slice(0, 8);

  return (
    <div>
      <NovaHero slide={hero} news={heroNews} />
      <div className="mx-auto max-w-7xl px-4 md:px-6 space-y-16 md:space-y-20 pt-16 md:pt-20 relative z-10">
        <HomeTopGames />
        <AdSlot style={{ minHeight: 120 }} />
        <Section>
          <Header eyebrow="Compete" title="All Tournaments" to="/tournaments" />
          {tours.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-tournaments">{tours.slice(0, 8).map((t, i) => <TournamentCard key={t.id} t={t} i={i} />)}</div>
            : <div className="card-surface rounded-2xl p-10 text-center text-white/50">No tournaments yet — be the first to create one.</div>}
        </Section>
        <HomeTopStreamers />
        <ProCTA />
        <GameOfWeek />
        <Section><Header eyebrow="Keep reading" title="Latest from the Newsroom" to="/news" label="All news" /><NewsFeed /></Section>
      </div>
    </div>
  );
}
